import { useState, useEffect, useRef } from 'react';
import type { Etapa, Card } from '../../types';
import { XMarkIcon, ChevronDownIcon, ChevronUpIcon, ArrowsPointingOutIcon, ArrowsPointingInIcon } from '@heroicons/react/24/outline';
import { googleDriveService } from '../../services/googleDriveClean';
import { automationEngine } from '../../utils/automationEngine';
import { useToast } from '../../contexts/ToastContext';
import { useIntegracoes } from '../../hooks/useIntegracoes';
import { apiUrl } from '../../utils/apiBase';
import { RotasField } from '../common/AeroportoSearch';
import { RotaTerrestreField, CidadeSearch } from '../common/RotaTerrestreField';
import { ViajantesField, problemaViajantes, diariasDaViagem, pedePluxee, viagemUrgente } from '../common/ViagensCampos';
import { tituloAutomatico, etiquetasAutomaticas } from '../../utils/tituloAutomatico';
import RichTextInput from '../RichTextInput';
import CampoParcelas from '../CampoParcelas';
import CampoItens from '../CampoItens';
import CampoNdVinculada from '../CampoNdVinculada';
import { ehAdminMaster } from '../../utils/adminMaster';
import {
  AvisoFornecedorNovo, ModalCienciaFornecedorNovo, cienciaValePara, textoLimpoFornecedor,
  verificarFornecedor, type CienciaFornecedorNovo,
} from '../AvisoFornecedorNovo';

// Resolve placeholders de data dinâmica usados em modelos recorrentes
// ({{hoje}}, {{hoje±Nd}}, {{hoje±Nw}}, {{hoje±Nm}}, {{dia_mes:N}}, {{ultimo_dia_mes}}, {{dia}}, {{mes}}, {{ano}}).
// Espelha a resolução feita no servidor para que a criação manual mostre datas reais.
const resolverPlaceholdersData = (texto: any): any => {
  if (typeof texto !== 'string') return texto;
  const agora = new Date();
  const fmt = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  let s = texto.replace(/\{\{hoje([+-]\d+[dmw])?\}\}/g, (_m, off) => {
    const d = new Date(agora);
    if (off) {
      const sign = off[0] === '+' ? 1 : -1;
      const qty = parseInt(off.slice(1, -1), 10) * sign;
      const unit = off[off.length - 1];
      if (unit === 'd') d.setDate(d.getDate() + qty);
      else if (unit === 'w') d.setDate(d.getDate() + qty * 7);
      else if (unit === 'm') d.setMonth(d.getMonth() + qty);
    }
    return fmt(d);
  });
  s = s.replace(/\{\{dia_mes:(\d{1,2})\}\}/g, (_m, n) => {
    const diaN = Math.min(31, Math.max(1, parseInt(n, 10)));
    let y = agora.getFullYear();
    let mes = agora.getMonth();
    if (agora.getDate() > diaN) {
      mes += 1;
      if (mes > 11) { mes = 0; y += 1; }
    }
    const ultimo = new Date(y, mes + 1, 0).getDate();
    return fmt(new Date(y, mes, Math.min(diaN, ultimo)));
  });
  s = s.replace(/\{\{dia_mes_corrente:(\d{1,2})\}\}/g, (_m, n) => {
    const diaN = Math.min(31, Math.max(1, parseInt(n, 10)));
    const ultimo = new Date(agora.getFullYear(), agora.getMonth() + 1, 0).getDate();
    return fmt(new Date(agora.getFullYear(), agora.getMonth(), Math.min(diaN, ultimo)));
  });
  s = s.replace(/\{\{ultimo_dia_mes\}\}/g, () => fmt(new Date(agora.getFullYear(), agora.getMonth() + 1, 0)));
  s = s.replace(/\{\{dia\}\}/g, String(agora.getDate()).padStart(2, '0'))
       .replace(/\{\{mes\}\}/g, String(agora.getMonth() + 1).padStart(2, '0'))
       .replace(/\{\{ano\}\}/g, String(agora.getFullYear()));
  return s;
};

interface NovoCartaoProps {
  etapa: Etapa;
  onSave: (cardData: Omit<Card, 'id'>) => void;
  onClose: () => void;
  empresaId?: string;
  automacoes?: any[]; // Lista de automações para esta etapa
  flowName?: string; // Nome do fluxo para organização de anexos
  flowId?: string; // ID do fluxo para carregar documentos
  usuario?: { id?: string; nome?: string; email?: string } | null; // quem está criando (registro de ciência)
  podeVerRestritos?: boolean; // administrador: vê campos visivelSomenteExtras
}

const NovoCartao = ({ etapa, onSave, onClose, empresaId, automacoes = [], flowName = 'Geral', flowId, usuario, podeVerRestritos = false }: NovoCartaoProps) => {
  // 🔒 visivelSomenteExtras: só quem está em visivelParaExtras vê o campo (a lista
  // traz id de user, id de colaborador ou e-mail). Admin e admin master passam.
  const campoRestritoOculto = (campo: { visivelSomenteExtras?: boolean; visivelParaExtras?: string[] }): boolean => {
    if (!campo.visivelSomenteExtras || podeVerRestritos || ehAdminMaster(usuario?.email)) return false;
    const meus = [String(usuario?.id || ''), String((usuario as any)?._id || ''), String(usuario?.email || '').toLowerCase()].filter(Boolean);
    return !(campo.visivelParaExtras || []).some(p => meus.includes(String(p)) || meus.includes(String(p).toLowerCase()));
  };
  // Ciência de fornecedor novo. Fica num ref porque o botão chama handleSubmit
  // direto do clique (o 1º argumento é o evento, não dá para passar "já confirmou").
  const cienciaRef = useRef<CienciaFornecedorNovo | null>(null);
  const [pedindoCiencia, setPedindoCiencia] = useState<{ campoId: string; fornecedor: string; parecidos: any[] } | null>(null);
  const { success, error } = useToast();
  const { getIntegracaoPorId } = useIntegracoes();
  const [titulo, setTitulo] = useState('');
  const [valores, setValores] = useState<Record<string, any>>({});
  // ✈️ Viagens (29/10/2026): "Solicitante" = quem está criando o card (não editável)
  useEffect(() => {
    const nome = String(usuario?.nome || '').trim();
    if (!nome) return;
    const auto = (etapa.campos || []).filter((c: any) => c.autoSolicitante);
    if (!auto.length) return;
    setValores(prev => {
      if (auto.every((c: any) => prev[c.id] === nome)) return prev;
      const novos = { ...prev };
      auto.forEach((c: any) => { novos[c.id] = nome; });
      return novos;
    });
  }, [etapa, usuario?.nome]);
  // ✈️ Viagens: diárias de alimentação saem do período da viagem quando pedem a diária Pluxee.
  // Quem digitou à mão manda: só troca valor vazio ou o que o próprio cálculo pôs.
  const diariasAutoRef = useRef<Record<string, string>>({});
  useEffect(() => {
    const alvos = (etapa.campos || []).filter((c: any) => c.diariasAutomaticas);
    if (!alvos.length || !pedePluxee(valores)) return;
    const dias = diariasDaViagem(valores);
    if (dias == null) return;
    const novo = String(dias);
    const mudar = alvos.filter((c: any) => {
      const atual = String(valores[c.id] ?? '').trim();
      return atual !== novo && (!atual || atual === diariasAutoRef.current[c.id]);
    });
    if (!mudar.length) return;
    mudar.forEach((c: any) => { diariasAutoRef.current[c.id] = novo; });
    setValores(prev => {
      const n = { ...prev };
      mudar.forEach((c: any) => { n[c.id] = novo; });
      return n;
    });
  }, [valores, etapa]);
  // 📝 Observação opcional por ANEXO, só no checklist de Lojas/CDs. O portal
  // desse mesmo fluxo já pede observação, mas uma por campo; aqui é por arquivo,
  // que é como a loja descreve cada foto ("vidro 2: trinca no canto").
  // A chave usa nome+tamanho em vez do índice: apagar um anexo do meio
  // reindexa a lista e faria a observação pular para o arquivo errado.
  const [observacoesAnexos, setObservacoesAnexos] = useState<Record<string, string>>({});
  const ehChecklistLojas = (() => {
    // Casar por "cd" + "loja" pegaria junto o fluxo "Solicitação lojas & CDs".
    // O alvo é só o checklist — cujo nome está gravado com o typo "ChekList".
    const n = String(flowName || '').toLowerCase().replace(/[^a-z]/g, '');
    return n.includes('cheklist') || n.includes('checklist');
  })();
  const chaveObsAnexo = (campoId: string, file: any) =>
    `${campoId}::${file?.name || ''}::${file?.size || 0}`;
  const [modeloSelecionadoId, setModeloSelecionadoId] = useState<string>('');
  // 🔒 Lock global: evita uploads simultâneos em campos diferentes (iOS/Android)
  const [uploadingCampoId, setUploadingCampoId] = useState<string | null>(null);

  // Verificar se algum campo está marcado como "usar como título"
  const campoTitulo = etapa.campos?.find(c => c.usarComoTitulo);
  // A etapa mostra o campo título padrão? (default: true para retrocompatibilidade)
  // título montado pelo tipo do card (tituloAutomatico na etapa) — o campo Título some
  const tituloAuto = (etapa as any).tituloAutomatico ? tituloAutomatico((etapa as any).tituloAutomatico, valores) : null;
  const exibirTituloPadrao = !campoTitulo && !(etapa as any).tituloAutomatico && (etapa.mostrarCampoTitulo !== false);
  // Título derivado: se tiver campo marcado, usa o valor dele; senão usa o state manual
  const tituloDerivado = tituloAuto || (campoTitulo ? (valores[campoTitulo.id] || '').toString().trim() : titulo.trim());
  const [camposVisiveis, setCamposVisiveis] = useState<Record<string, boolean>>({});
  // ✈️ Viagens (Dayane 30/10/2026): campo com soViagemUrgente (ex.: "Justificativa de viagem urgente")
  // só aparece — e só é cobrado — quando a 1ª data da viagem está a 20 dias ou menos de hoje.
  useEffect(() => {
    const alvos = (etapa.campos || []).filter((c: any) => c.soViagemUrgente);
    if (!alvos.length) return;
    const urgente = viagemUrgente(valores);
    setCamposVisiveis(prev => {
      if (alvos.every((c: any) => (prev[c.id] !== false) === urgente)) return prev;
      const n = { ...prev };
      alvos.forEach((c: any) => { n[c.id] = urgente; });
      return n;
    });
    // camposVisiveis entra para refazer depois do "tudo visível" inicial da etapa
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valores, etapa, camposVisiveis]);
  const [opcoesEspecificas, setOpcoesEspecificas] = useState<Record<string, Record<string, boolean>>>({});
  const [destinoAutomacao, setDestinoAutomacao] = useState<string | null>(null);
  // Campo "responsavel": opções são todo mundo do fluxo, não só desta etapa
  // (união dos participantes de todas as etapas). Só busca quando precisa.
  const [colaboradoresDoFluxo, setColaboradoresDoFluxo] = useState<any[]>([]);
  const temCampoResponsavel = etapa.campos?.some(c => c.tipo === 'responsavel');
  useEffect(() => {
    if (!temCampoResponsavel) return;
    Promise.all([
      fetch(apiUrl('/colaboradores')).then((r) => r.json()),
      flowId ? fetch(apiUrl(`/flows/${flowId}?excludeCards=true`)).then((r) => r.json()) : Promise.resolve(null),
    ]).then(([colabData, flowData]) => {
      const todosColaboradores = colabData?.data || colabData?.colaboradores || [];
      const etapasDoFluxo = flowData?.data?.etapas?.length ? flowData.data.etapas : [etapa];
      const idsParticipantes = new Set<string>();
      etapasDoFluxo.forEach((e: any) => (e.participantes || []).forEach((id: any) => idsParticipantes.add(String(id))));
      setColaboradoresDoFluxo(todosColaboradores.filter((c: any) => idsParticipantes.has(String(c.id))));
    }).catch(() => undefined);
  }, [temCampoResponsavel, flowId]);
  const [campoGatilhoAutomacao, setCampoGatilhoAutomacao] = useState<string | null>(null); // 🔧 Campo que trigou a automação
  const [checkboxExpandido, setCheckboxExpandido] = useState<Record<string, boolean>>({});
  const [salvando, setSalvando] = useState(false);
  const [documentosDisponiveis, setDocumentosDisponiveis] = useState<any[]>([]);
  const [documentoExpandido, setDocumentoExpandido] = useState<string | null>(null);
  const [loadingDocumentos, setLoadingDocumentos] = useState(false);
  const [modalExpandido, setModalExpandido] = useState(false);
  const [docFullscreen, setDocFullscreen] = useState<any | null>(null);
  const [documentosVisiveis, setDocumentosVisiveis] = useState<Record<string, boolean>>({});

  // Carregar documentos habilitados para esta etapa
  useEffect(() => {
    const carregarDocumentos = async () => {
      if (!etapa.documentosHabilitados || etapa.documentosHabilitados.length === 0) {
        setDocumentosDisponiveis([]);
        return;
      }
      try {
        setLoadingDocumentos(true);
        const url = `/api/documentos/templates?flowId=${flowId || ''}&empresaId=${empresaId || ''}`;
        const response = await fetch(url);
        if (response.ok) {
          const todosTemplates = await response.json();
          const templatesHabilitados = todosTemplates.filter((template: any) => {
            const templateId = template._id || template.id;
            return etapa.documentosHabilitados?.includes(templateId);
          });
          setDocumentosDisponiveis(templatesHabilitados);
          // Descobrir quais docs são alvo de automações exibir_ocultar
          const docsControlados = new Set<string>();
          automacoes.forEach((aut: any) => {
            aut.acoes?.forEach((acao: any) => {
              if (acao.tipo === 'exibir_ocultar') {
                acao.configuracao?.regras?.forEach((r: any) => {
                  r.elementosAfetados?.forEach((e: any) => { if (e.id?.startsWith('doc_')) docsControlados.add(e.id); });
                  r.elementosAfetadosSenao?.forEach((e: any) => { if (e.id?.startsWith('doc_')) docsControlados.add(e.id); });
                  r.acoesEntaoAdicionais?.forEach((ex: any) => { if (ex.elemento?.id?.startsWith('doc_')) docsControlados.add(ex.elemento.id); });
                  r.acoesSenaoAdicionais?.forEach((ex: any) => { if (ex.elemento?.id?.startsWith('doc_')) docsControlados.add(ex.elemento.id); });
                });
              }
            });
          });
          // Docs controlados por automação começam ocultos; os demais, visíveis
          const visInicial: Record<string, boolean> = {};
          templatesHabilitados.forEach((doc: any) => {
            const docKey = `doc_${doc._id || doc.id}`;
            visInicial[docKey] = !docsControlados.has(docKey);
          });
          setDocumentosVisiveis(visInicial);
          // Re-executar automações após docs carregarem para aplicar regras
          if (automacoes.length > 0) {
            setTimeout(() => {
              automationEngine.executeAutomations(
                automacoes, etapa, valores, 'initial',
                (targetFieldId: string, visible: boolean, opcEsp?: Record<string, boolean>) => {
                  if (targetFieldId.startsWith('doc_')) {
                    setDocumentosVisiveis(prev => ({ ...prev, [targetFieldId]: visible }));
                  } else {
                    setCamposVisiveis(prev => ({ ...prev, [targetFieldId]: visible }));
                  }
                  if (opcEsp) {
                    const opcMap: Record<string, boolean> = { ...opcEsp, __default: !visible };
                    setOpcoesEspecificas(prev => ({ ...prev, [targetFieldId]: opcMap }));
                  }
                },
                () => {},
                undefined
              );
            }, 50);
          }
        }
      } catch (err) {
        console.error('Erro ao carregar documentos:', err);
      } finally {
        setLoadingDocumentos(false);
      }
    };
    carregarDocumentos();
  }, [etapa.id, etapa.documentosHabilitados, flowId, empresaId]);

  // Inicializar campos visíveis (todos visíveis por padrão)
  useEffect(() => {
    const visibilidadeInicial: Record<string, boolean> = {};
    etapa.campos?.forEach(campo => {
      visibilidadeInicial[campo.id] = true; // Todos visíveis por padrão
    });
    setCamposVisiveis(visibilidadeInicial);
  }, [etapa.campos]);

  // Executar automações quando campos mudarem
  const didRunInitialAutomation = useRef<boolean>(false);

  // 1) Mantém todos os campos visíveis por padrão ao montar/alterar etapa
  //    (evita flicker até que a automação rode)
  useEffect(() => {
    const camposIniciaisVisiveis: Record<string, boolean> = {};
    etapa.campos?.forEach(campo => {
      camposIniciaisVisiveis[campo.id] = true;
    });
    setCamposVisiveis(camposIniciaisVisiveis);
    // permitir nova execução inicial se a etapa mudar
    didRunInitialAutomation.current = false;
  }, [etapa.id]);

  // 2) Executa as automações UMA VEZ quando o modal abre (ou etapa muda)
  useEffect(() => {
    if (!didRunInitialAutomation.current && automacoes.length > 0) {
      didRunInitialAutomation.current = true;
      // Executa com 'initial' para avaliar todas as regras com os valores atuais (vazios no início)
      setTimeout(() => {
        automationEngine.executeAutomations(
          automacoes,
          etapa,
          {}, // valores iniciais vazios - as automações serão executadas quando os campos forem preenchidos
          'initial',
          (targetFieldId: string, visible: boolean, opcEsp?: Record<string, boolean>) => {
            if (targetFieldId.startsWith('doc_')) {
              setDocumentosVisiveis(prev => ({ ...prev, [targetFieldId]: visible }));
            } else {
              setCamposVisiveis(prev => ({ ...prev, [targetFieldId]: visible }));
            }
            if (opcEsp) {
              const opcMap: Record<string, boolean> = { ...opcEsp, __default: !visible };
              setOpcoesEspecificas(prev => ({ ...prev, [targetFieldId]: opcMap }));
            }
          },
          (etapaDestinoId: string) => {
            // Guardar sugestão de movimentação; engine já evita no 'initial'
            setDestinoAutomacao(etapaDestinoId);
          },
          undefined
        );
      }, 0);
    }
  }, [automacoes, etapa.id]); // Removido 'valores' para evitar execuções múltiplas

  // Função para atualizar valores e executar automações
  const updateFieldValue = (fieldId: string, value: any) => {
    setValores(prev => {
      const newValues = { ...prev, [fieldId]: value };
      
      // Executar automações após a atualização do valor
      {
        setTimeout(() => {
          automationEngine.executeAutomations(
            automacoes,
            etapa,
            newValues,
            fieldId,
            (targetFieldId: string, visible: boolean, opcEsp?: Record<string, boolean>) => {
              if (targetFieldId.startsWith('doc_')) {
                setDocumentosVisiveis(prev => ({ ...prev, [targetFieldId]: visible }));
              } else {
                setCamposVisiveis(prevVisibility => ({
                  ...prevVisibility,
                  [targetFieldId]: visible
                }));
              }
              if (opcEsp) {
                const opcMap: Record<string, boolean> = { ...opcEsp, __default: !visible };
                setOpcoesEspecificas(prev => ({ ...prev, [targetFieldId]: opcMap }));
              }
            },
            (etapaDestinoId: string, meta?: { campoGatilho?: string }) => {
              setDestinoAutomacao(etapaDestinoId);
              setCampoGatilhoAutomacao(meta?.campoGatilho || null); // 🔧 Salvar campo gatilho
            },
            undefined
          );
        }, 0);
      }
      
      return newValues;
    });
  };

  // Verificar se uma opção específica de um campo é visível
  const isOpcaoVisivel = (campoId: string, opcao: string): boolean => {
    const opcMap = opcoesEspecificas[campoId];
    if (!opcMap) return true; // Sem regras = tudo visível
    if (opcMap[opcao] !== undefined) return opcMap[opcao];
    if (opcMap.__default !== undefined) return opcMap.__default;
    return true;
  };

  const handleSubmit = async () => {
    // Só validar título se tiver campo título visível ou campo marcado como título
    if ((exibirTituloPadrao || campoTitulo) && !tituloDerivado) {
      error(campoTitulo ? `Preencha o campo "${campoTitulo.nome}" (usado como título)` : 'Digite um título para o cartão');
      return;
    }

    // ⚠️ Fornecedor novo: pergunta a ciência antes de criar. Se a verificação
    // não responder, segue sem perguntar — o aviso ajuda, nunca trava o trabalho.
    const campoFornecedor = etapa.campos.find(c =>
      c.validarFornecedorNovo && camposVisiveis[c.id] !== false && textoLimpoFornecedor(valores[c.id]));
    if (campoFornecedor) {
      const nomeDigitado = textoLimpoFornecedor(valores[campoFornecedor.id]);
      if (!cienciaValePara(cienciaRef.current, nomeDigitado)) {
        cienciaRef.current = null;
        setSalvando(true);
        const verificacao = await verificarFornecedor(nomeDigitado);
        setSalvando(false);
        if (verificacao?.novo) {
          setPedindoCiencia({ campoId: campoFornecedor.id, fornecedor: nomeDigitado,
                              parecidos: verificacao.parecidos || [] });
          return;
        }
      }
    }

    // Ativar loading
    setSalvando(true);

    // Debug: Mostrar todos os valores dos campos
    console.log('🔍 Debug - Valores dos campos:', valores);
    console.log('🔍 Debug - Campos da etapa:', etapa.campos);

    // ✈️ Viajantes: nome + CPF válido para cada um
    for (const campo of etapa.campos.filter((c: any) => c.tipo === 'viajantes' && camposVisiveis[c.id] !== false)) {
      const lista = Array.isArray(valores[campo.id]) ? valores[campo.id].filter((x: any) => x && (String(x.nome || '').trim() || String(x.cpf || '').trim())) : [];
      if (!campo.obrigatorio && lista.length === 0) continue;
      const problema = problemaViajantes(valores[campo.id]);
      if (problema) {
        error(`${campo.nome}: ${problema}`);
        setSalvando(false);
        return;
      }
    }

    // Verifica campos obrigatórios com validação específica por tipo
    // Campos ocultos por automação (Exibir/Ocultar) são ignorados na validação
    const camposObrigatorios = etapa.campos.filter(campo => campo.obrigatorio && camposVisiveis[campo.id] !== false);
    const camposFaltando = camposObrigatorios.filter(campo => {
      const valor = valores[campo.id];
      
      // Validação específica por tipo de campo
      switch (campo.tipo) {
        case 'checkbox':
        case 'checkbox_multi':
        case 'parcelas':
        case 'itens':
        case 'nd_vinculada':
        case 'progresso':
        case 'viajantes':
          // Para checkboxes múltiplos, deve ter pelo menos uma opção selecionada
          return !Array.isArray(valor) || valor.length === 0;
        case 'checkbox_single':
          // Para checkbox único, deve ter uma opção selecionada
          return !valor || valor === '';
        case 'texto':
        case 'texto_longo':
        case 'numero':
        case 'data':
        case 'valor':
        case 'lista':
          // Para campos de texto/lista, não pode estar vazio
          return !valor || valor.toString().trim() === '';
        case 'anexo':
          // Para anexos, deve ter pelo menos um arquivo
          return !Array.isArray(valor) || valor.length === 0;
        default:
          return !valor;
      }
    });    if (camposFaltando.length > 0) {
      error(`Preencha os campos obrigatórios: ${camposFaltando.map(c => c.nome).join(', ')}`);
      setSalvando(false);
      return;
    }

    // 📎 Quantidade mínima de anexos por campo (ex.: 3 referências comerciais)
    const anexosInsuficientes = etapa.campos
      .filter(campo => campo.tipo === 'anexo' && camposVisiveis[campo.id] !== false && Number(campo.minAnexos || 0) > 0)
      .map(campo => {
        const valor = valores[campo.id];
        const qtd = Array.isArray(valor) ? valor.length : valor ? 1 : 0;
        return { campo, qtd, min: Number(campo.minAnexos || 0) };
      })
      .filter(item => item.qtd < item.min);

    if (anexosInsuficientes.length > 0) {
      error(
        `Anexos insuficientes: ${anexosInsuficientes
          .map(i => `${i.campo.nome} (${i.qtd}/${i.min})`)
          .join(', ')}`
      );
      setSalvando(false);
      return;
    }

    // Processar arquivos para upload antes de salvar o card (apenas se houver integração)
    const valoresProcessados = { ...valores };
    
    for (const [campoId, valor] of Object.entries(valores)) {
      const campo = etapa.campos.find(c => c.id === campoId);
      if (campo?.tipo === 'anexo' && valor && Array.isArray(valor) && valor.length > 0) {
        const integracaoId = campo.integracaoId; // Usar integracaoId do campo configurado na etapa (se houver)
        
        // Se não houver integração, apenas salvar os arquivos localmente
        if (!integracaoId) {
          console.log(`📎 Salvando ${valor.length} arquivo(s) localmente (sem integração)`);
          valoresProcessados[campoId] = valor.map((arquivo: any) => ({
            ...arquivo,
            status: 'local',
            savedAt: new Date()
          }));
          continue; // Pular para o próximo campo
        }
        
        // Se houver integração, fazer upload
        console.log(`📤 Iniciando upload de ${valor.length} arquivo(s) para integração ${integracaoId}`);
        
        try {
          // Buscar integração já carregada pelo hook
          const integracao = getIntegracaoPorId(integracaoId);
          console.log('🔍 Integração encontrada:', integracao);
            
            if (integracao) {
              console.log(`📁 Enviando arquivos para pasta: ${integracao.pastaNome}`);
              
              // Processar cada arquivo
              console.log('🔄 Iniciando Promise.all para arquivos...');
              const arquivosProcessados = await Promise.all(
                valor.map(async (arquivo: any) => {
                  try {
                    console.log(`⬆️ Enviando arquivo: ${arquivo.name} para ${integracao.pastaNome}`);
                    
                    // Converter para DriveFile (formato esperado pelo serviço)
                    const driveFile = {
                      name: arquivo.name,
                      size: arquivo.size || 0,
                      type: arquivo.type || 'application/octet-stream',
                      dataUrl: arquivo.dataUrl
                    };
                    
                    // Upload real usando a integração configurada
                    console.log(`🔄 Fazendo upload real usando integração: ${integracao.nome}`);
                    
                    // Preparar dados específicos da integração
                    const userInfo = {
                      nome: 'Usuário Escalone',
                      email: 'usuario@escalone.com', 
                      empresa: 'Escalone'
                    };
                    
                    // Para integrações do tipo email, usar os dados da configuração
                    if (integracao.tipo === 'email') {
                      const payload = {
                        file: driveFile,
                        usuario: userInfo,
                        emailDestino: integracao.emailDestino,
                        nomeConfigurador: integracao.nomeConfigurador
                      };
                      
                      console.log('� Enviando com configuração personalizada:', payload.emailDestino);
                      
                      const response = await fetch(apiUrl('/upload-arquivo'), {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(payload),
                      });

                      if (!response.ok) {
                        throw new Error(`Erro HTTP: ${response.status}`);
                      }

                      const uploadResult = await response.json();
                      
                      if (uploadResult.status === 'success') {
                        console.log(`✅ Upload realizado com sucesso:`, uploadResult);
                      } else {
                        throw new Error(uploadResult.message || 'Upload falhou');
                      }
                    } else {
                      // Usar serviço padrão para outras integrações
                      const uploadResult = await googleDriveService.uploadFile(
                        driveFile,
                        integracao.pastaId || undefined
                      );
                      
                      if (uploadResult.status !== 'success') {
                        throw new Error(uploadResult.message || 'Erro desconhecido no upload');
                      }
                    }
                    
                    console.log(`✅ Upload realizado com sucesso para ${integracao.nome}`);
                    return {
                      ...arquivo,
                      integracaoId,
                      integracaoNome: integracao.nome,
                      pastaNome: integracao.pastaNome || integracao.nomeConfigurador,
                      pastaPath: integracao.pastaPath,
                      uploadedAt: new Date(),
                      status: 'uploaded',
                      emailDestino: integracao.emailDestino || integracao.email
                    };
                  } catch (error) {
                    console.error(`❌ Erro ao enviar arquivo ${arquivo.name}:`, error);
                    return {
                      ...arquivo,
                      integracaoId,
                      status: 'error',
                      error: error instanceof Error ? error.message : String(error)
                    };
                  }
                })
              );
              
              valoresProcessados[campoId] = arquivosProcessados;
              console.log(`✅ Upload concluído para ${arquivosProcessados.length} arquivo(s)`);
            } else {
              console.log('❌ Integração não encontrada para ID:', integracaoId);
              // Salvar localmente se integração não for encontrada
              valoresProcessados[campoId] = valor.map((arquivo: any) => ({
                ...arquivo,
                status: 'local',
                savedAt: new Date(),
                warning: 'Integração configurada não encontrada'
              }));
            }
          } catch (error) {
            console.error('❌ Erro no processo de upload:', error);
            // Em caso de erro, salvar localmente
            valoresProcessados[campoId] = valor.map((arquivo: any) => ({
              ...arquivo,
              status: 'error',
              savedAt: new Date(),
              error: error instanceof Error ? error.message : String(error)
            }));
          }
      }
    }
    
    // ⚠️ NÃO limpar o campo gatilho aqui!
    // O campo gatilho será limpo pelo KanbanBoard APÓS a automação mover o card
    // Se limparmos aqui, o handleCreateCard não conseguirá avaliar a condição da automação
    
    const etapaDestinoFinal = destinoAutomacao || etapa.id;

    // 📝 Observações por anexo -> campo "Informações adicionais".
    // Mesmo destino que o portal deste fluxo já usa, para a loja não ter que
    // procurar a mesma informação em dois lugares diferentes.
    if (ehChecklistLojas) {
      const linhas: string[] = [];
      (etapa.campos || []).forEach(campo => {
        if (campo.tipo !== 'anexo') return;
        const arquivos = Array.isArray(valores[campo.id]) ? valores[campo.id] : [];
        arquivos.forEach((file: any) => {
          const obs = (observacoesAnexos[chaveObsAnexo(campo.id, file)] || '').trim();
          if (obs) linhas.push(`${campo.nome} — ${file?.name || 'anexo'}: ${obs}`);
        });
      });
      if (linhas.length > 0) {
        const campoInfo = (etapa.campos || []).find(c =>
          (c.tipo === 'texto_longo' || c.tipo === 'texto') &&
          String(c.nome || '').toLowerCase().includes('inform')
        );
        const bloco = linhas.join('\n');
        if (campoInfo) {
          const atual = String(valoresProcessados[campoInfo.id] || '').trim();
          valoresProcessados[campoInfo.id] = atual ? `${atual}\n\n${bloco}` : bloco;
        }
      }
    }

    // Se a etapa tem um campo tipo "responsavel" e a pessoa escolheu alguém,
    // isso vale mais que o responsável padrão configurado na etapa.
    const campoResponsavelId = etapa.campos?.find(c => c.tipo === 'responsavel')?.id;
    const responsavelEscolhido = campoResponsavelId ? valoresProcessados[campoResponsavelId] : undefined;

    const novoCard = {
      titulo: tituloDerivado,
      etapaId: etapa.id, // ← SEMPRE criar na etapa original, automação move depois
      campos: valoresProcessados,
      responsavelIndividualId: responsavelEscolhido || etapa.responsavelPadraoId || undefined, // Atribuir responsável padrão da etapa
      createdAt: new Date(),
      updatedAt: new Date(),
      empresaId: empresaId || etapa.empresaId || '',
      ...((etapa as any).etiquetaPorCampo ? { etiquetasIds: etiquetasAutomaticas((etapa as any).etiquetaPorCampo, valoresProcessados, []) } : {}),
      // registro de quem confirmou estar ciente do fornecedor novo
      ...(cienciaRef.current ? { cienciaFornecedorNovo: cienciaRef.current } : {}),
    };
    
    // Adicionar card ao estado IMEDIATAMENTE (sincronamente)
    onSave(novoCard);
    
    // Fechar modal e desativar loading
    setSalvando(false);
    onClose();
    success('Cartão criado com sucesso.');
    
    // Salvar em background (a função onSave já dispara o salvamento)
    // Após salvar no backend, as automações serão executadas automaticamente
    
    // Log para debugging
    console.log('🆕 Novo card criado com campos:', {
      titulo: tituloDerivado,
      campoTitulo: campoTitulo?.nome || 'manual',
      etapaId: etapa.id,
      campos: valores,
      totalCampos: Object.keys(valores).length,
      camposEtapa: etapa.campos.length,
      destinoAutomacao
    });
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 z-[9999] overflow-y-auto" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className={`relative z-10 overflow-hidden bg-white border border-gray-200/80 rounded-2xl shadow-[0_25px_60px_-15px_rgba(0,0,0,0.3)] w-full ${modalExpandido ? 'max-w-5xl max-h-[96vh]' : 'max-w-lg max-h-[92vh]'} flex flex-col my-auto transition-all duration-300`}>
        {/* Top gradient accent bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-gray-800 via-black to-gray-800" />

        {/* Header */}
        <div className="bg-gradient-to-b from-gray-50/80 to-white border-b border-gray-200 px-5 py-3 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-gray-800 to-black flex items-center justify-center shadow-sm">
                <span className="text-white text-sm">+</span>
              </div>
              <div>
                <h2 className="text-base font-bold text-gray-900">Novo Cartão</h2>
                <p className="text-[10px] text-gray-400">Etapa: {etapa.nome}</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setModalExpandido(!modalExpandido)}
                className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100 transition-colors animate-pulse"
                title={modalExpandido ? 'Reduzir' : 'Expandir'}
              >
                {modalExpandido ? <ArrowsPointingInIcon className="h-4 w-4" /> : <ArrowsPointingOutIcon className="h-4 w-4" />}
              </button>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Conteúdo */}
        <div className="px-5 py-4 overflow-y-auto flex-1">
          <div className="space-y-4">
            {/* Seletor de Modelo (Cards Personalizados) */}
            {(etapa.cardsPersonalizados && etapa.cardsPersonalizados.length > 0) && (
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">
                  Modelo
                </label>
                <select
                  value={modeloSelecionadoId}
                  onChange={(e) => {
                    const tplId = e.target.value;
                    setModeloSelecionadoId(tplId);
                    const tpl = tplId ? etapa.cardsPersonalizados?.find(t => t.id === tplId) : null;
                    // Reset completo: template sobrescreve tudo; "Em branco" limpa tudo
                    // Placeholders de data dinâmica (modelos recorrentes) são resolvidos para datas reais.
                    const novosValores: Record<string, any> = {};
                    Object.entries(tpl?.valores || {}).forEach(([k, v]) => { novosValores[k] = resolverPlaceholdersData(v); });
                    setValores(novosValores);
                    setTitulo(tpl?.titulo ? resolverPlaceholdersData(tpl.titulo) : '');

                    // Re-executar automações com os novos valores para reaplicar exibir/ocultar
                    if (automacoes.length > 0) {
                      setTimeout(() => {
                        // Resetar visibilidade para default (todos visíveis) antes de re-executar
                        const visibilidadeInicial: Record<string, boolean> = {};
                        etapa.campos?.forEach(c => { visibilidadeInicial[c.id] = true; });
                        setCamposVisiveis(visibilidadeInicial);
                        setOpcoesEspecificas({});

                        automationEngine.executeAutomations(
                          automacoes,
                          etapa,
                          novosValores,
                          'initial',
                          (targetFieldId: string, visible: boolean, opcEsp?: Record<string, boolean>) => {
                            if (targetFieldId.startsWith('doc_')) {
                              setDocumentosVisiveis(prev => ({ ...prev, [targetFieldId]: visible }));
                            } else {
                              setCamposVisiveis(prev => ({ ...prev, [targetFieldId]: visible }));
                            }
                            if (opcEsp) {
                              const opcMap: Record<string, boolean> = { ...opcEsp, __default: !visible };
                              setOpcoesEspecificas(prev => ({ ...prev, [targetFieldId]: opcMap }));
                            }
                          },
                          (etapaDestinoId: string) => {
                            setDestinoAutomacao(etapaDestinoId);
                          },
                          undefined
                        );
                      }, 0);
                    }
                  }}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-black/30 focus:border-gray-800 transition-all bg-white"
                >
                  <option value="">Em branco</option>
                  {etapa.cardsPersonalizados.map(tpl => (
                    <option key={tpl.id} value={tpl.id}>{tpl.nome}</option>
                  ))}
                </select>
                <p className="text-[10px] text-gray-400 mt-1">Selecione um modelo para pré-preencher os campos. Mude para "Em branco" para limpar tudo.</p>
              </div>
            )}

            {/* Título - só aparece se "Exibir campo título" estiver ativo e nenhum campo tiver usarComoTitulo */}
            {exibirTituloPadrao && (
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">
                Título
              </label>
              <input
                value={titulo}
                onChange={(e) => setTitulo(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-black/30 focus:border-gray-800 transition-all bg-white placeholder-gray-300"
                placeholder="Digite o título do cartão"
              />
            </div>
            )}

            {/* Campos */}
            {etapa.campos.map((campo) => {
              if (campoRestritoOculto(campo)) return null;
              const isVisible = camposVisiveis[campo.id] !== false;
              // Para documentos, checar visibilidade no documentosVisiveis
              if (campo.tipo === 'documento') {
                const docTemplateId = campo.documentoTemplateId || campo.id.replace('doc_', '');
                const docVisible = documentosVisiveis[`doc_${docTemplateId}`] !== false;
                if (!docVisible) return null;
                const doc = documentosDisponiveis.find((d: any) => (d._id || d.id) === docTemplateId);
                if (!doc) return null;
                const docId = doc._id || doc.id;
                const isExpanded = documentoExpandido === docId;
                const isPdf = doc.tipo === 'pdf' && doc.pdfUrl;
                return (
                  <div key={campo.id} className="border border-gray-200 bg-white rounded-lg overflow-hidden">
                    <div className="flex items-center justify-between px-3 py-2 bg-gradient-to-r from-blue-50 to-white">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span className="text-sm">{isPdf ? '📄' : '📝'}</span>
                        <div className="min-w-0">
                          <h4 className="font-medium text-gray-900 text-xs truncate">{doc.nome}</h4>
                          {doc.descricao && <p className="text-[10px] text-gray-500 truncate">{doc.descricao}</p>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button type="button" onClick={() => setDocumentoExpandido(isExpanded ? null : docId)} className="px-2 py-1 text-[10px] bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors font-medium">{isExpanded ? 'Fechar' : 'Ver'}</button>
                        <button type="button" onClick={() => setDocFullscreen(doc)} className="px-2 py-1 text-[10px] bg-purple-100 text-purple-700 rounded hover:bg-purple-200 transition-colors font-medium flex items-center gap-0.5" title="Tela cheia"><ArrowsPointingOutIcon className="h-3 w-3" /></button>
                        {isPdf && <a href={doc.pdfUrl} target="_blank" rel="noopener noreferrer" className="px-2 py-1 text-[10px] bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors font-medium">Baixar</a>}
                      </div>
                    </div>
                    {isExpanded && (
                      <div className="border-t border-gray-200 bg-gray-50">
                        {isPdf ? (
                          <iframe src={doc.pdfUrl} className="w-full h-[50vh] border-0" title={`PDF - ${doc.nome}`} />
                        ) : (
                          <div className="p-3 max-h-[50vh] overflow-y-auto">
                            <div className="bg-white p-3 rounded shadow-sm">
                              <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: doc.conteudoHtml || '' }} />
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              }
              if (!isVisible) return null;
              
              return (
              <div key={campo.id}>
                {!(campo.ocultarTitulo && campo.tipo === 'texto_informativo') && (
                <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-semibold text-gray-600">
                  {campo.tipo === 'data_vencimento' ? (
                    <span className="text-red-500 flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-400"></span>
                      {campo.nome}
                    </span>
                  ) : campo.usarComoTitulo ? (
                    <span className="text-amber-600 flex items-center gap-1">
                      ⭐ {campo.nome}
                      <span className="text-[9px] font-normal text-amber-400 ml-1">(título do cartão)</span>
                    </span>
                  ) : (
                    campo.nome
                  )}
                  {campo.obrigatorio && <span className="text-red-400 ml-0.5">*</span>}
                </label>
                {['checkbox_single', 'lista', 'checkbox', 'checkbox_multi', 'progresso'].includes(campo.tipo) && valores[campo.id] && (Array.isArray(valores[campo.id]) ? valores[campo.id].length > 0 : valores[campo.id] !== '') && (
                  <button
                    type="button"
                    onClick={() => updateFieldValue(campo.id, Array.isArray(valores[campo.id]) ? [] : '')}
                    className="text-[11px] text-red-500 bg-red-50 hover:text-white hover:bg-red-500 px-2 py-0.5 rounded-full transition-colors flex items-center gap-1 font-medium border border-red-200 hover:border-red-500"
                    title="Limpar campo"
                  >
                    ✕ Limpar
                  </button>
                )}
                </div>
                )}

                {campo.tipo === 'texto' && (campo as any).autoSolicitante && (
                  <div className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 text-gray-700">
                    {valores[campo.id] || usuario?.nome || '—'}
                  </div>
                )}
                {campo.tipo === 'texto' && !(campo as any).autoSolicitante && (campo as any).buscaCidade && (
                  <CidadeSearch value={valores[campo.id] || ''} onChange={(v) => updateFieldValue(campo.id, v)} placeholder="🔍 Buscar cidade..." />
                )}
                {campo.tipo === 'viajantes' && (
                  <ViajantesField valor={valores[campo.id]} onChange={(novo) => updateFieldValue(campo.id, novo)} />
                )}
                {campo.tipo === 'texto' && !(campo as any).autoSolicitante && !(campo as any).buscaCidade && (
                  <RichTextInput
                    value={valores[campo.id] || ''}
                    onChange={(html) => updateFieldValue(campo.id, html)}
                    placeholder="Digite aqui..."
                    minHeight="60px"
                  />
                )}
                {/* fornecedor que nunca apareceu em card nenhum: avisa, sugere grafia parecida, não trava */}
                {['texto', 'texto_longo'].includes(campo.tipo) && campo.validarFornecedorNovo && (
                  <AvisoFornecedorNovo
                    nome={valores[campo.id] || ''}
                    onUsarNome={(nome) => updateFieldValue(campo.id, nome)} />
                )}

                {campo.tipo === 'texto_longo' && (
                  <RichTextInput
                    value={valores[campo.id] || ''}
                    onChange={(html) => updateFieldValue(campo.id, html)}
                    placeholder="Digite aqui..."
                    minHeight="140px"
                  />
                )}

                {campo.tipo === 'telefone' && (() => {
                  const formatarTelefone = (valor: string) => {
                    const numeros = valor.replace(/\D/g, '');
                    const n = numeros.slice(0, 11);
                    if (n.length <= 2) return n;
                    if (n.length <= 6) return `(${n.slice(0, 2)}) ${n.slice(2)}`;
                    if (n.length <= 10) return `(${n.slice(0, 2)}) ${n.slice(2, 6)}-${n.slice(6)}`;
                    return `(${n.slice(0, 2)}) ${n.slice(2, 7)}-${n.slice(7)}`;
                  };
                  return (
                    <div className="relative">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400">🇧🇷</span>
                      <input
                        type="tel"
                        value={formatarTelefone(valores[campo.id] || '')}
                        onChange={(e) => updateFieldValue(campo.id, formatarTelefone(e.target.value))}
                        className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-black/30 focus:border-gray-800 transition-all bg-white placeholder-gray-300"
                        placeholder="(99) 99999-9999"
                      />
                    </div>
                  );
                })()}

                {campo.tipo === 'valor' && (
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-xs text-gray-400 font-medium">R$</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={(() => {
                        const raw = String(valores[campo.id] || '');
                        if (!raw) return '';
                        let digits = raw.replace(/\D/g, '');
                        if (!digits || digits === '0' || digits === '00') return '';
                        digits = digits.replace(/^0+/, '') || '0';
                        digits = digits.padStart(3, '0');
                        if (digits.length > 12) digits = digits.slice(0, 12);
                        const dec = digits.slice(-2);
                        let intPart = digits.slice(0, -2);
                        intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
                        return `${intPart},${dec}`;
                      })()}
                      onChange={(e) => {
                        let digits = e.target.value.replace(/\D/g, '');
                        if (!digits) { updateFieldValue(campo.id, ''); return; }
                        digits = digits.replace(/^0+/, '') || '0';
                        if (digits.length > 12) digits = digits.slice(0, 12);
                        digits = digits.padStart(3, '0');
                        const dec = digits.slice(-2);
                        let intPart = digits.slice(0, -2);
                        intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
                        updateFieldValue(campo.id, `${intPart},${dec}`);
                      }}
                      className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-black/30 focus:border-gray-800 transition-all bg-white placeholder-gray-300"
                      placeholder="0,00"
                    />
                  </div>
                )}

                {campo.tipo === 'numero' && (
                  <input
                    type="text"
                    inputMode="numeric"
                    value={valores[campo.id] || ''}
                    onChange={(e) => updateFieldValue(campo.id, e.target.value.replace(/\D/g, ''))}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-black/30 focus:border-gray-800 transition-all bg-white placeholder-gray-300"
                    placeholder="Apenas números"
                  />
                )}

                {campo.tipo === 'data' && (
                  <input
                    type="datetime-local"
                    value={valores[campo.id] || ''}
                    onChange={(e) => updateFieldValue(campo.id, e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-black/30 focus:border-gray-800 transition-all bg-white"
                  />
                )}

                {campo.tipo === 'data_vencimento' && (
                  <input
                    type="datetime-local"
                    value={valores[campo.id] || ''}
                    onChange={(e) => updateFieldValue(campo.id, e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-red-200 rounded-lg focus:ring-2 focus:ring-red-400/40 focus:border-red-300 transition-all bg-red-50/50"
                    placeholder="Selecione a data de vencimento"
                  />
                )}

                {campo.tipo === 'lista' && (
                  <select
                    value={valores[campo.id] || ''}
                    onChange={(e) => updateFieldValue(campo.id, e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-black/30 focus:border-gray-800 transition-all bg-white"
                  >
                    <option value="">Selecione...</option>
                    {campo.opcoes?.filter(opcao => isOpcaoVisivel(campo.id, opcao)).map((opcao) => (
                      <option key={opcao} value={opcao}>{opcao}</option>
                    ))}
                  </select>
                )}

                {(campo.tipo === 'checkbox' || campo.tipo === 'checkbox_multi') && (
                  <div className="space-y-1 p-2.5 bg-gray-50/80 rounded-lg border border-gray-100">
                    {(() => {
                      const LIMITE_PADRAO = 5;
                      const todasOpcoes = (campo.opcoes || []).filter(opcao => isOpcaoVisivel(campo.id, opcao));
                      const temMuitas = todasOpcoes.length > LIMITE_PADRAO;
                      const isExpanded = checkboxExpandido[campo.id] || false;
                      const opcoesVisiveis = isExpanded ? todasOpcoes : todasOpcoes.slice(0, LIMITE_PADRAO);

                      return (
                        <>
                          {temMuitas && (
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs text-gray-500">
                                {todasOpcoes.length} opções
                              </span>
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => {
                                    const currentValues = valores[campo.id] || [];
                                    const allSelected = todasOpcoes.every((o: string) => currentValues.includes(o));
                                    updateFieldValue(campo.id, allSelected ? [] : [...todasOpcoes]);
                                  }}
                                  className="text-xs inline-flex items-center gap-1 text-gray-700 hover:text-gray-900 px-2 py-1 rounded hover:bg-gray-100"
                                >
                                  {(valores[campo.id] || []).length === todasOpcoes.length ? 'Desmarcar todos' : 'Marcar todos'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setCheckboxExpandido(prev => ({ ...prev, [campo.id]: !isExpanded }))}
                                  className="text-xs inline-flex items-center gap-1 text-gray-700 hover:text-gray-900 px-2 py-1 rounded hover:bg-gray-100"
                                  aria-label={isExpanded ? 'Recolher opções' : 'Expandir opções'}
                                >
                                {isExpanded ? (
                                  <>
                                    <ChevronUpIcon className="h-3 w-3" /> Recolher
                                  </>
                                ) : (
                                  <>
                                    <ChevronDownIcon className="h-3 w-3" /> Expandir
                                  </>
                                )}
                                </button>
                              </div>
                            </div>
                          )}

                          <div className={isExpanded ? 'max-h-48 overflow-y-auto pr-1 space-y-0.5' : 'space-y-0.5'}>
                            {opcoesVisiveis.map((opcao) => (
                              <label key={opcao} className="flex items-center gap-2 hover:bg-gray-100 p-1.5 rounded transition-colors">
                                <input
                                  type="checkbox"
                                  checked={valores[campo.id]?.includes(opcao) || false}
                                  onChange={(e) => {
                                    const currentValues = valores[campo.id] || [];
                                    const newValues = e.target.checked
                                      ? [...currentValues, opcao]
                                      : currentValues.filter((v: string) => v !== opcao);
                                    updateFieldValue(campo.id, newValues);
                                  }}
                                  className="h-3.5 w-3.5 rounded border-gray-300 text-black focus:ring-black transition-colors"
                                />
                                <span className="text-xs">{opcao}</span>
                              </label>
                            ))}
                          </div>

                          {!isExpanded && temMuitas && (
                            <button
                              type="button"
                              onClick={() => setCheckboxExpandido(prev => ({ ...prev, [campo.id]: true }))}
                              className="text-xs text-gray-700 hover:text-gray-900 px-2 py-1 rounded hover:bg-gray-100"
                            >
                              Mostrar mais...
                            </button>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}

                {campo.tipo === 'checkbox_single' && (
                  <div className="space-y-0.5 p-2.5 bg-gray-50/80 rounded-lg border border-gray-100">
                    {campo.opcoes?.filter(opcao => isOpcaoVisivel(campo.id, opcao)).map((opcao) => (
                      <label key={opcao} className="flex items-center gap-2 hover:bg-gray-100 p-1.5 rounded-md transition-colors">
                        <input
                          type="radio"
                          name={campo.id}
                          checked={valores[campo.id] === opcao}
                          onChange={() => updateFieldValue(campo.id, opcao)}
                          className="h-3.5 w-3.5 border-gray-300 text-black focus:ring-black transition-colors"
                        />
                        <span className="text-xs">{opcao}</span>
                      </label>
                    ))}
                  </div>
                )}

                {/* parcelas: quem cria define a lista; pagar é no financeiro */}
                {campo.tipo === 'parcelas' && (
                  <CampoParcelas
                    valor={valores[campo.id]}
                    opcoesPadrao={campo.opcoes || []}
                    onChange={(novo) => updateFieldValue(campo.id, novo)}
                    podeEditarLista
                    podeMarcarPaga={false}
                    semearPadrao />
                )}

                {/* itens: cada linha com fornecedor; aprovar por fornecedor é na etapa de aprovação */}
                {campo.tipo === 'itens' && (
                  <CampoItens
                    valor={valores[campo.id]}
                    onChange={(novo) => updateFieldValue(campo.id, novo)}
                    podeEditarLista
                    podeAprovar={false}
                    usuario={usuario ? { id: (usuario as any).id, nome: (usuario as any).nome || (usuario as any).name } : null}
                    contextoUpload={{ flowId: 'novos_cards', campoId: campo.id, flowNome: flowName || 'Geral', cardTitulo: tituloDerivado || 'Novo Card' }} />
                )}

                {/* 🧾 ND vinculada (30/10/2026): verba do fornecedor que paga/abate a compra */}
                {(campo.tipo as string) === 'nd_vinculada' && (() => {
                  const cfgNd = ((campo as any).ndConfig || {}) as { verbasFlowId?: string; valorUsadoCampoId?: string; valorCardCampoId?: string };
                  return (
                    <CampoNdVinculada
                      valor={valores[campo.id]}
                      onChange={(novo) => updateFieldValue(campo.id, novo)}
                      podeEditar
                      config={cfgNd}
                      compraFlowId={flowId}
                      valorUsado={cfgNd.valorUsadoCampoId ? valores[cfgNd.valorUsadoCampoId] : undefined}
                      valorCard={cfgNd.valorCardCampoId ? valores[cfgNd.valorCardCampoId] : undefined}
                      onValorUsado={cfgNd.valorUsadoCampoId ? (v) => updateFieldValue(cfgNd.valorUsadoCampoId as string, v) : undefined} />
                  );
                })()}

                {/* responsavel: escolhido aqui vira o responsavelIndividualId do card ao criar */}
                {campo.tipo === 'responsavel' && (
                  <select
                    value={valores[campo.id] || ''}
                    onChange={(e) => updateFieldValue(campo.id, e.target.value)}
                    className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-black focus:border-transparent transition-all"
                  >
                    <option value="">Selecione...</option>
                    {colaboradoresDoFluxo.map((c: any) => (
                      <option key={c.id} value={c.id}>{c.nome}{c.cargo ? ` - ${c.cargo}` : ''}</option>
                    ))}
                  </select>
                )}

                {campo.tipo === 'progresso' && (
                  <div className="space-y-0.5 p-2.5 bg-gray-50/80 rounded-lg border border-gray-100">
                    {campo.opcoes?.map((tarefa) => (
                      <label key={tarefa} className="flex items-center gap-2 hover:bg-gray-100 p-1.5 rounded-md transition-colors">
                        <input
                          type="checkbox"
                          checked={valores[campo.id]?.includes(tarefa) || false}
                          onChange={(e) => {
                            const currentValues = valores[campo.id] || [];
                            const newValues = e.target.checked
                              ? [...currentValues, tarefa]
                              : currentValues.filter((v: string) => v !== tarefa);
                            updateFieldValue(campo.id, newValues);
                          }}
                          className="h-3.5 w-3.5 rounded border-gray-300 text-black focus:ring-black transition-colors"
                        />
                        <span className="text-xs">{tarefa}</span>
                      </label>
                    ))}
                    <div className="text-sm text-gray-500 mt-2">
                      Progresso: {campo.opcoes ? 
                        Math.round((valores[campo.id]?.length || 0) / campo.opcoes.length * 100) 
                        : 0}%
                    </div>
                  </div>
                )}                {campo.tipo === 'anexo' && (() => {
                  // 📦 LIMITES de anexo
                  const MAX_ANEXOS = Number(campo.maxAnexos || 0) || 6;
                  const MIN_ANEXOS = Number(campo.minAnexos || 0);
                  const MAX_SIZE_MB = 20;
                  const MAX_SIZE = MAX_SIZE_MB * 1024 * 1024;
                  const anexosAtuais = valores[campo.id] || [];
                  const anexosCount = Array.isArray(anexosAtuais) ? anexosAtuais.length : 0;
                  const espacoDisponivel = MAX_ANEXOS - anexosCount;

                  // Handler compartilhado entre o input de arquivo e o input de câmera
                  const processarArquivos = async (files: File[], inputEl?: HTMLInputElement | null) => {
                    if (files.length === 0) return;
                    if (uploadingCampoId && uploadingCampoId !== campo.id) {
                      alert('⏳ Aguarde o upload em andamento terminar antes de adicionar outro anexo.');
                      if (inputEl) inputEl.value = '';
                      return;
                    }
                    setUploadingCampoId(campo.id);
                    try {
                      if (files.length > espacoDisponivel) {
                        alert(`⚠️ Limite de anexos!\n\nVocê pode adicionar no máximo ${espacoDisponivel} arquivo(s).\nVocê selecionou ${files.length}.\n\nMáximo por campo: ${MAX_ANEXOS} arquivos.`);
                        if (inputEl) inputEl.value = '';
                        return;
                      }
                      const arquivosGrandes = files.filter(f => f.size > MAX_SIZE);
                      if (arquivosGrandes.length > 0) {
                        alert(`⚠️ Arquivo(s) muito grande(s)!\n\nMáximo: ${MAX_SIZE_MB}MB por arquivo.\n\n${arquivosGrandes.map(f => f.name).join(', ')}`);
                        if (inputEl) inputEl.value = '';
                        return;
                      }
                      const cardTempId = 'novo_' + Date.now();
                      const fileData = await Promise.all(
                        files.map(async (file) => {
                          try {
                            const formData = new FormData();
                            formData.append('file', file);
                            formData.append('flowId', 'novos_cards');
                            formData.append('cardId', cardTempId);
                            formData.append('campoId', campo.id);
                            formData.append('flowNome', flowName || 'Geral');
                            formData.append('cardTitulo', tituloDerivado || 'Novo Card');
                            const response = await fetch('/api/upload-anexo-local', {
                              method: 'POST',
                              body: formData
                            });
                            if (!response.ok) throw new Error('Erro no upload');
                            const result = await response.json();
                            return {
                              name: result.originalName || file.name,
                              fileName: result.fileName,
                              size: result.size || file.size,
                              type: result.type || file.type,
                              url: result.url,
                              uploadedAt: result.uploadedAt,
                              integracaoId: campo.integracaoId
                            };
                          } catch (error) {
                            console.error('Erro upload:', error);
                            alert(`Erro ao enviar ${file.name}`);
                            return null;
                          }
                        })
                      );
                      const sucessos = fileData.filter(f => f !== null);
                      if (sucessos.length > 0) {
                        const atuais = Array.isArray(valores[campo.id]) ? valores[campo.id] : [];
                        updateFieldValue(campo.id, [...atuais, ...sucessos]);
                      }
                      if (inputEl) inputEl.value = '';
                    } finally {
                      setUploadingCampoId(null);
                    }
                  };

                  return (
                  <div className="space-y-2">
                    {/* Mostrar integração configurada (opcional) */}
                    {campo.integracaoId && (
                      <div className="text-xs text-gray-600 bg-gray-100 p-1.5 rounded" title="Integração configurada">
                        📁 Integração configurada
                      </div>
                    )}
                    
                    {/* Contador de anexos */}
                    <div className={`text-[10px] px-2 py-0.5 rounded ${anexosCount >= MAX_ANEXOS ? 'bg-red-50 text-red-600' : (MIN_ANEXOS > 0 && anexosCount < MIN_ANEXOS) ? 'bg-amber-50 text-amber-700' : 'bg-gray-50 text-gray-500'}`}>
                      📎 {anexosCount}/{MAX_ANEXOS} anexos{MIN_ANEXOS > 0 ? ` • mínimo ${MIN_ANEXOS}` : ''} • Máx. {MAX_SIZE_MB}MB
                    </div>

                    {/* 🔒 Indicadores de upload em andamento */}
                    {uploadingCampoId && uploadingCampoId !== campo.id && (
                      <div className="text-[10px] px-2 py-1 rounded bg-amber-50 text-amber-700 border border-amber-200">
                        ⏳ Aguarde o upload em andamento terminar antes de adicionar outro anexo...
                      </div>
                    )}
                    {uploadingCampoId === campo.id && (
                      <div className="text-[10px] px-2 py-1 rounded bg-blue-50 text-blue-700 border border-blue-200 animate-pulse">
                        📤 Enviando arquivo(s)... não feche esta tela.
                      </div>
                    )}

                    {/* Input de arquivo - UPLOAD LOCAL */}
                    <input
                      type="file"
                      multiple
                      accept="image/*,video/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.zip,.rar"
                      disabled={anexosCount >= MAX_ANEXOS || (uploadingCampoId !== null && uploadingCampoId !== campo.id)}
                      onChange={(e) => processarArquivos(Array.from(e.target.files || []), e.target)}
                      className={`w-full px-3 py-2 text-xs border rounded-lg focus:ring-2 focus:ring-black/30 focus:border-gray-800 transition-all file:mr-2 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-medium ${anexosCount >= MAX_ANEXOS ? 'border-red-200 bg-red-50/50 cursor-not-allowed file:bg-gray-300 file:text-gray-500' : 'border-gray-200 file:bg-black file:text-white hover:file:bg-gray-800'}`}
                    />

                    {/* 📋 Área de colar (Ctrl+V): pega o print da área de transferência
                        e manda pro mesmo processarArquivos do input acima — mesma
                        validação de limite/tamanho, nada duplicado aqui. */}
                    {!(anexosCount >= MAX_ANEXOS || (uploadingCampoId !== null && uploadingCampoId !== campo.id)) && (
                      <div
                        tabIndex={0}
                        role="button"
                        title="Clique aqui e pressione Ctrl+V para colar um print"
                        onPaste={(e) => {
                          const arquivos = Array.from(e.clipboardData?.items || [])
                            .filter(item => item.kind === 'file')
                            .map(item => item.getAsFile())
                            .filter((f): f is File => !!f);
                          if (arquivos.length === 0) return;
                          e.preventDefault();

                          // Print da área de transferência vem sem nome útil ("image.png").
                          // Renomeia com timestamp pra não colidir no storage.
                          const carimbo = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
                          const renomeados = arquivos.map((f, i) => {
                            const ext = (f.name?.includes('.') ? f.name.split('.').pop() : (f.type.split('/')[1] || 'png')) || 'png';
                            const nome = f.name && f.name !== 'image.png'
                              ? f.name
                              : `print-${carimbo}${arquivos.length > 1 ? `-${i + 1}` : ''}.${ext}`;
                            return new File([f], nome, { type: f.type, lastModified: f.lastModified });
                          });

                          processarArquivos(renomeados);
                        }}
                        className="group w-full px-2 py-2 text-[10px] text-center text-gray-500 border border-dashed border-gray-300 rounded-lg cursor-pointer outline-none hover:border-gray-400 hover:bg-gray-50 focus:border-black focus:bg-gray-50 focus:text-gray-700 transition-colors"
                      >
                        <span className="group-focus:hidden">📋 Clique aqui para colar um print (Ctrl+V)</span>
                        <span className="hidden group-focus:inline">📋 Pronto — pressione Ctrl+V para colar o print</span>
                      </div>
                    )}

                    {/* 📷 Botão dedicado de Câmera (mobile) - força abrir câmera direto */}
                    <div className="flex gap-2">
                      <label
                        className={`relative flex-1 cursor-pointer flex items-center justify-center gap-1 px-3 py-2 text-xs rounded-lg border transition-all ${anexosCount >= MAX_ANEXOS || (uploadingCampoId !== null && uploadingCampoId !== campo.id) ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 active:bg-gray-100'}`}
                      >
                        📷 Tirar foto
                        <input
                          type="file"
                          accept="image/*"
                          capture="environment"
                          disabled={anexosCount >= MAX_ANEXOS || (uploadingCampoId !== null && uploadingCampoId !== campo.id)}
                          onChange={(e) => processarArquivos(Array.from(e.target.files || []), e.target)}
                          style={{ position: 'absolute', inset: 0, opacity: 0, width: '100%', height: '100%', cursor: 'pointer' }}
                        />
                      </label>
                      <label
                        className={`relative flex-1 cursor-pointer flex items-center justify-center gap-1 px-3 py-2 text-xs rounded-lg border transition-all ${anexosCount >= MAX_ANEXOS || (uploadingCampoId !== null && uploadingCampoId !== campo.id) ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 active:bg-gray-100'}`}
                      >
                        🎬 Gravar vídeo
                        <input
                          type="file"
                          accept="video/*"
                          capture="environment"
                          disabled={anexosCount >= MAX_ANEXOS || (uploadingCampoId !== null && uploadingCampoId !== campo.id)}
                          onChange={(e) => processarArquivos(Array.from(e.target.files || []), e.target)}
                          style={{ position: 'absolute', inset: 0, opacity: 0, width: '100%', height: '100%', cursor: 'pointer' }}
                        />
                      </label>
                    </div>
                    
                    {valores[campo.id] && valores[campo.id].length > 0 && (
                      <div className="text-xs text-gray-600">
                        {valores[campo.id].length} arquivo(s):
                        <div className="mt-0.5 space-y-0.5">
                          {valores[campo.id].map((file: any, index: number) => (
                            <div key={index} className="p-1 bg-gray-100 rounded text-[10px] space-y-1">
                              <div className="flex items-center justify-between gap-1">
                                <span className="truncate flex-1" title={file.name}>📎 {file.name} ({Math.round(file.size / 1024)}KB)</span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const atuais = Array.isArray(valores[campo.id]) ? valores[campo.id] : [];
                                    const novos = atuais.filter((_: any, i: number) => i !== index);
                                    updateFieldValue(campo.id, novos);
                                    setObservacoesAnexos(prev => {
                                      const copia = { ...prev };
                                      delete copia[chaveObsAnexo(campo.id, file)];
                                      return copia;
                                    });
                                  }}
                                  className="shrink-0 px-1.5 py-0.5 text-[10px] text-red-600 hover:bg-red-50 rounded transition-colors"
                                  title="Remover anexo"
                                >
                                  ✕
                                </button>
                              </div>
                              {ehChecklistLojas && (
                                <textarea
                                  value={observacoesAnexos[chaveObsAnexo(campo.id, file)] || ''}
                                  onChange={(e) => setObservacoesAnexos(prev => ({
                                    ...prev,
                                    [chaveObsAnexo(campo.id, file)]: e.target.value,
                                  }))}
                                  rows={2}
                                  placeholder="Observação deste anexo (opcional)"
                                  className="w-full px-2 py-1 text-[10px] border border-gray-300 rounded resize-none bg-white focus:outline-none focus:ring-1 focus:ring-gray-400"
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  );
                })()}

                {campo.tipo === 'justificativa_checkbox' && (() => {
                  // Buscar o campo checkbox vinculado
                  const campoVinculado = campo.campoVinculadoId ? etapa.campos?.find(c => c.id === campo.campoVinculadoId) : null;
                  const itensSelecionados = (campoVinculado && valores[campoVinculado.id] && Array.isArray(valores[campoVinculado.id])) ? valores[campoVinculado.id] : [];
                  
                  // Garantir que apenas itens selecionados tenham justificativas
                  const justificativasAtuais = (valores[campo.id] && typeof valores[campo.id] === 'object') ? valores[campo.id] : {};
                  const justificativasFiltradas: Record<string, string> = Object.fromEntries(
                    Object.entries(justificativasAtuais).filter(([item]) => itensSelecionados.includes(item))
                  ) as Record<string, string>;
                  
                  return (
                    <div className="space-y-3">
                      {!campoVinculado ? (
                        <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                          ⚠️ Campo checkbox vinculado não encontrado
                        </div>
                      ) : itensSelecionados.length === 0 ? (
                        <div className="text-sm text-gray-500 bg-gray-50 p-3 rounded-lg">
                          Nenhum item selecionado em "{campoVinculado.nome}"
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <div className="text-xs text-gray-600 mb-2">
                            Justifique cada item selecionado em "{campoVinculado.nome}":
                          </div>
                          {itensSelecionados.map((item: string) => (
                            <div key={item} className="space-y-1">
                              <label className="block text-sm font-medium text-gray-700">
                                📌 {item}
                              </label>
                              <textarea
                                value={justificativasFiltradas[item] || ''}
                                onChange={(e) => {
                                  const newValue = {
                                    ...justificativasFiltradas,
                                    [item]: e.target.value
                                  };
                                  updateFieldValue(campo.id, newValue);
                                }}
                                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-black/30 focus:border-gray-800 transition-all resize-none bg-white"
                                rows={2}
                                placeholder={`Descreva o motivo para "${item}"...`}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {campo.tipo === 'rotas' && (
                  <RotasField
                    rotasAtuais={Array.isArray(valores[campo.id]) ? valores[campo.id] : []}
                    campoId={campo.id}
                    updateFieldValue={updateFieldValue}
                    terrestre={!!(campo as any).terrestre}
                  />
                )}

                {campo.tipo === 'rota_terrestre' && (
                  <RotaTerrestreField
                    rotasAtuais={Array.isArray(valores[campo.id]) ? valores[campo.id] : []}
                    campoId={campo.id}
                    updateFieldValue={updateFieldValue}
                    consumoMedioPadrao={campo.consumoMedioPadrao}
                    precoCombustivelPadrao={campo.precoCombustivelPadrao}
                  />
                )}

                {campo.tipo === 'texto_informativo' && (
                  <div className={`px-3 py-2 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg ${campo.formatacao?.alinhamento === 'centro' ? 'text-center' : campo.formatacao?.alinhamento === 'direita' ? 'text-right' : 'text-left'}`}>
                    <span style={{ fontWeight: campo.formatacao?.negrito ? 'bold' : 'normal', fontStyle: campo.formatacao?.italico ? 'italic' : 'normal', fontSize: campo.formatacao?.tamanho === 'pequeno' ? '11px' : campo.formatacao?.tamanho === 'grande' ? '16px' : '13px', color: campo.formatacao?.cor || '#374151' }}>
                      {campo.textoInformativo || ''}
                    </span>
                  </div>
                )}
              </div>
              ); // Fechamento do return do map
            })}
          </div>

          {/* 📄 Documentos Disponíveis (legado - só mostra docs que não estão como campos) */}
          {(() => {
            const docIdsInCampos = new Set(etapa.campos.filter(c => c.tipo === 'documento').map(c => c.documentoTemplateId || c.id.replace('doc_', '')));
            const docsLegado = documentosDisponiveis.filter((doc) => {
              const docId = doc._id || doc.id;
              return !docIdsInCampos.has(docId) && documentosVisiveis[`doc_${docId}`] !== false;
            });
            if (docsLegado.length === 0) return null;
            return (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <h3 className="text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wider flex items-center gap-1.5">
                📄 Documentos Disponíveis
              </h3>
              
              {loadingDocumentos ? (
                <div className="text-center py-3">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-800 mx-auto"></div>
                  <p className="text-xs text-gray-500 mt-1">Carregando...</p>
                </div>
              ) : (
                <div className="space-y-1.5">
                  {docsLegado.map((doc) => {
                    const docId = doc._id || doc.id;
                    const isExpanded = documentoExpandido === docId;
                    const isPdf = doc.tipo === 'pdf' && doc.pdfUrl;
                    
                    return (
                      <div key={docId} className="border border-gray-200 bg-white rounded-lg overflow-hidden">
                        <div className="flex items-center justify-between px-3 py-2 bg-gradient-to-r from-blue-50 to-white">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <span className="text-sm">{isPdf ? '📄' : '📝'}</span>
                            <div className="min-w-0">
                              <h4 className="font-medium text-gray-900 text-xs truncate">{doc.nome}</h4>
                              {doc.descricao && (
                                <p className="text-[10px] text-gray-500 truncate">{doc.descricao}</p>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <button
                              type="button"
                              onClick={() => setDocumentoExpandido(isExpanded ? null : docId)}
                              className="px-2 py-1 text-[10px] bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors font-medium"
                            >
                              {isExpanded ? 'Fechar' : 'Ver'}
                            </button>
                            <button
                              type="button"
                              onClick={() => setDocFullscreen(doc)}
                              className="px-2 py-1 text-[10px] bg-purple-100 text-purple-700 rounded hover:bg-purple-200 transition-colors font-medium flex items-center gap-0.5"
                              title="Tela cheia"
                            >
                              <ArrowsPointingOutIcon className="h-3 w-3" />
                            </button>
                            {isPdf && (
                              <a
                                href={doc.pdfUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-2 py-1 text-[10px] bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors font-medium"
                              >
                                Baixar
                              </a>
                            )}
                          </div>
                        </div>
                        
                        {isExpanded && (
                          <div className="border-t border-gray-200 bg-gray-50">
                            {isPdf ? (
                              <iframe
                                src={doc.pdfUrl}
                                className="w-full h-[50vh] border-0"
                                title={`PDF - ${doc.nome}`}
                              />
                            ) : (
                              <div className="p-3 max-h-[50vh] overflow-y-auto">
                                <div className="bg-white p-3 rounded shadow-sm">
                                  <div
                                    className="prose prose-sm max-w-none"
                                    dangerouslySetInnerHTML={{ __html: doc.conteudoHtml || '' }}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
          })()}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2 flex-shrink-0 bg-gradient-to-t from-gray-50/80 to-white rounded-b-2xl">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            disabled={salvando}
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 text-sm bg-black text-white rounded-lg hover:bg-gray-800 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 font-medium"
            disabled={salvando}
          >
            {salvando && (
              <svg className="animate-spin h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            {salvando ? 'Salvando...' : 'Criar Cartão'}
          </button>
        </div>
      </div>

      {pedindoCiencia && (
        // o clique tem que morrer aqui: este modal é filho do overlay do formulário,
        // que fecha no clique de fora — sem isto, clicar fora da caixa jogava fora
        // o cartão inteiro que a pessoa acabou de preencher
        <div onClick={(e) => e.stopPropagation()}>
        <ModalCienciaFornecedorNovo
          fornecedor={pedindoCiencia.fornecedor}
          parecidos={pedindoCiencia.parecidos}
          onVoltar={() => setPedindoCiencia(null)}
          onUsarNome={(nome) => {
            // trocou pelo nome já conhecido: não é mais fornecedor novo
            updateFieldValue(pedindoCiencia.campoId, nome);
            cienciaRef.current = null;
            setPedindoCiencia(null);
          }}
          onCiente={() => {
            cienciaRef.current = {
              fornecedor: pedindoCiencia.fornecedor,
              campoId: pedindoCiencia.campoId,
              cienteEm: new Date().toISOString(),
              cientePor: usuario?.id || null,
              cientePorNome: usuario?.nome || usuario?.email || null,
            };
            setPedindoCiencia(null);
            handleSubmit();
          }} />
        </div>
      )}

      {/* Fullscreen Document Overlay */}
      {docFullscreen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[10000] flex flex-col" onClick={() => setDocFullscreen(null)}>
          <div className="flex items-center justify-between px-6 py-3 bg-white/95 border-b border-gray-200 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3">
              <span className="text-lg">{docFullscreen.tipo === 'pdf' && docFullscreen.pdfUrl ? '📄' : '📝'}</span>
              <div>
                <h3 className="font-semibold text-gray-900 text-sm">{docFullscreen.nome}</h3>
                {docFullscreen.descricao && <p className="text-xs text-gray-500">{docFullscreen.descricao}</p>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {docFullscreen.tipo === 'pdf' && docFullscreen.pdfUrl && (
                <a
                  href={docFullscreen.pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-1.5 text-xs bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors font-medium"
                >
                  Baixar PDF
                </a>
              )}
              <button
                onClick={() => setDocFullscreen(null)}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-auto" onClick={(e) => e.stopPropagation()}>
            {docFullscreen.tipo === 'pdf' && docFullscreen.pdfUrl ? (
              <iframe
                src={docFullscreen.pdfUrl}
                className="w-full h-full border-0"
                title={`PDF - ${docFullscreen.nome}`}
              />
            ) : (
              <div className="max-w-4xl mx-auto p-8">
                <div className="bg-white rounded-xl shadow-lg p-8">
                  <div
                    className="prose prose-lg max-w-none"
                    dangerouslySetInnerHTML={{ __html: docFullscreen.conteudoHtml || '' }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NovoCartao;

